interface FitmentFields { make: string; model: string; year: string }
interface Product extends FitmentFields { id: string }
interface Edit extends FitmentFields { aiGenerated: boolean }

export function validFitmentYear(value: string): boolean {
  return /^[1-9]\d{3}$/.test(value.trim());
}

/** Keep existing range/label tokens intact; never clamp explicit future years. */
export function appendFitmentYear(raw: string, value: string, currentYear = new Date().getFullYear()): string | null {
  const year = value.trim();
  if (!validFitmentYear(year)) throw new Error('Enter a four-digit year, for example 2027.');
  let items: string[];
  try {
    const parsed: unknown = JSON.parse(raw);
    items = (Array.isArray(parsed) ? parsed : [parsed])
      .flatMap(item => String(item ?? '').split(',')).map(item => item.trim()).filter(Boolean);
  } catch {
    items = raw.split(',').map(item => item.trim()).filter(Boolean);
  }
  const target = Number(year);
  const alreadyIncluded = items.some(item => {
    if (item === year) return true;
    const range = item.match(/^(\d{4})\s*[-–—]\s*(\d{4}|current|present)$/i);
    if (range) {
      const end = /^\d{4}$/.test(range[2]!) ? Number(range[2]) : currentYear;
      return target >= Number(range[1]) && target <= end;
    }
    const open = item.match(/^(\d{4})\s*\+$/);
    return !!open && target >= Number(open[1]) && target <= currentYear;
  });
  if (alreadyIncluded) return null;
  items = [...new Set([...items, year])];
  // Avoid the backend's numeric compression misreading preserved range tokens.
  if (items.length > 128) {
    const labels = items.filter(item => !/^\d{4}$/.test(item));
    const numbers = items.filter(item => /^\d{4}$/.test(item)).map(Number).sort((a, b) => a - b);
    const ranges: string[] = [];
    for (let i = 0; i < numbers.length; i++) {
      const start = numbers[i]!;
      let end = start;
      while (numbers[i + 1] === end + 1) end = numbers[++i]!;
      ranges.push(start === end ? String(start) : `${start}-${end}`);
    }
    items = [...labels, ...ranges];
    if (items.length > 128) throw new Error('This year list exceeds Shopify’s 128-item limit. Review its ranges before adding a year.');
  }
  return JSON.stringify(items);
}

export function planAddFitmentYear<P extends Product, E extends Edit>(
  products: P[], selectedIds: ReadonlySet<string>, edits: ReadonlyMap<string, E>, year: string,
) {
  const changes = new Map<string, E | Edit>();
  let skipped = 0;
  for (const product of products) {
    if (!selectedIds.has(product.id)) continue;
    const existing = edits.get(product.id) ?? {
      make: product.make, model: product.model, year: product.year, aiGenerated: false,
    };
    const nextYear = appendFitmentYear(existing.year, year);
    if (nextYear === null) { skipped++; continue; }
    changes.set(product.id, { ...existing, year: nextYear, aiGenerated: false });
  }
  return { changes, skipped };
}

/** Unknown/incomplete responses must not discard pending work. */
export function confirmedFitmentIds(
  submittedIds: string[],
  result: { successCount?: number; errorCount?: number; errors?: { productId: string }[] },
): Set<string> {
  const failed = new Set((result.errors ?? []).map(error => error.productId));
  const succeeded = submittedIds.filter(id => !failed.has(id));
  if (result.errorCount !== failed.size || result.successCount !== succeeded.length) return new Set();
  return new Set(succeeded);
}
